"use client";

import React, { useEffect, useState, useCallback } from "react";
import {
  TimerReset,
  X,
  Clock,
  Tag,
  Plus,
  Loader2,
  Check,
  Home,
  Briefcase,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import {
  FaDollarSign,
  FaEuroSign,
  FaPoundSign,
  FaRubleSign,
  FaShekelSign,
} from "react-icons/fa";
import toast from "react-hot-toast";
import EditServiceModal from "./EditServiceModal";
import { motion, AnimatePresence } from "framer-motion";
import CreateServiceBussiness from "./CreateServiceBussiness";
import ShowWorkersModal from "./ShowWorkersModal";
import { HiOutlineUserGroup } from "react-icons/hi2";
import AddListingModal from "./../../Auth/MyServices/AddListingModal";
import EditListingModal from "../../Auth/MyServices/EditListingModal";
import { REST_API_URL } from "../../api/config";

const API_URL = REST_API_URL;

const CURRENCY_ICON_MAP = {
  USD: FaDollarSign,
  EUR: FaEuroSign,
  GBP: FaPoundSign,
  RUB: FaRubleSign,
  ILS: FaShekelSign,
  AUD: FaDollarSign,
  CAD: FaDollarSign,
  BRL: FaDollarSign,
  MXN: FaDollarSign,
  AED: FaDollarSign,
  ARS: FaDollarSign,
  ZAR: FaDollarSign,
};

const getUserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

function DeleteConfirmationModal({ isOpen, onClose, onConfirm, listingName }) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] px-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Delete Listing
              </h3>

              <p className="text-gray-600 mb-6">
                Are you sure you want to delete ?
              </p>

              <div className="flex gap-3 w-full">
                <button
                  onClick={onClose}
                  className="flex-1 py-3 px-4 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={onConfirm}
                  className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white font-medium hover:from-red-600 hover:to-red-700 transition-all shadow-lg shadow-red-500/20"
                >
                  Delete
                </button>
              </div>

              <p className="text-xs text-gray-500 mt-4">
                This will permanently remove the listing from your account.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function DeleteServiceConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  serviceName,
}) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] px-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Delete Service
              </h3>

              <p className="text-gray-600 mb-6">
                Are you sure you want to delete service{" "}
                <span className="font-semibold text-gray-900">
                  {serviceName || "this service"}
                </span>
                ?
              </p>

              <div className="flex gap-3 w-full">
                <button
                  onClick={onClose}
                  className="flex-1 py-3 px-4 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={onConfirm}
                  className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white font-medium hover:from-red-600 hover:to-red-700 transition-all shadow-lg shadow-red-500/20"
                >
                  Delete
                </button>
              </div>

              <p className="text-xs text-gray-500 mt-4">
                This will permanently remove the service from your account.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ListingCard({ listing, onEdit, onDelete, userType }) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currencies, setCurrencies] = useState([]);

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const token =
          localStorage.getItem("accessToken") ||
          sessionStorage.getItem("accessToken") ||
          "";

        const headers = buildHeaders(token);
        
        const res = await fetch(
          `${API_URL}/service/currencies/`,
          { headers }
        );

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        setCurrencies(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Currency fetch error:", err);
        setCurrencies([]);
      }
    };

    fetchCurrencies();
  }, []);

  const getCurrencyCode = () => {
    const c = listing.currency;

    if (typeof c === "string") return c.toUpperCase();
    if (typeof c === "object" && c?.short_name)
      return c.short_name.toUpperCase();

    return "USD";
  };

  const currencyCode = getCurrencyCode();

  const currencyExistsInBase = currencies.some(
    (c) => c.short_name.toUpperCase() === currencyCode
  );

  const CurrencyIcon =
    currencyExistsInBase && CURRENCY_ICON_MAP[currencyCode]
      ? CURRENCY_ICON_MAP[currencyCode]
      : FaDollarSign;

  const listingType = listing.listing_type || "Rent";
  const propertyType = listing.property_type || "Apartment";
  const propertyName = listing.property_name || "No Name";
  const propertyDescription = listing.property_description || "No description";

  const sellPrice = Number(listing.sell_price || 0);
  const monthlyRentPrice = Number(listing.monthly_rent_price || 0);
  const yearlyRentPrice = Number(listing.yearly_rent_price || 0);

  const tourDuration = listing.tour_duration || 120;
  const bufferTime = listing.buffer_time || 30;

  const formatPrice = (amount) => {
    if (!amount && amount !== 0) return "—";

    if (amount >= 1_000_000) {
      const v = amount / 1_000_000;
      return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(2)}M`;
    }

    if (amount >= 1_000) {
      const v = amount / 1_000;
      return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}K`;
    }

    return amount.toLocaleString();
  };

  const displayPrice =
    listingType === "Sell"
      ? formatPrice(sellPrice)
      : formatPrice(monthlyRentPrice);

  const showAnnualPrice = listingType === "Rent" && yearlyRentPrice > 0;

  const handleDeleteClick = () => setShowDeleteModal(true);

  const handleConfirmDelete = () => {
    onDelete(listing.id);
    setShowDeleteModal(false);
  };

  return (
    <>
      <motion.div
        whileHover={{ y: -8, boxShadow: "0 20px 40px rgba(85,99,246,0.15)" }}
        transition={{ type: "spring", stiffness: 300, damping: 22 }}
        className="relative bg-white rounded-3xl p-6 border border-gray-100 hover:border-[#5563F6]/30 flex flex-col h-[auto]"
      >
        <div className="flex items-start justify-between mb-5">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-1">
              {propertyName}
            </h3>
            <span className="text-sm font-semibold text-[#5563F6] bg-[#EEF2FF] px-3 py-1 rounded-full">
              For {listingType === "Sell" ? "Sale" : "Rent"}
            </span>
          </div>

          {userType !== "worker" && (
            <button
              onClick={handleDeleteClick}
              className="text-gray-400 hover:text-red-500 p-2 rounded-lg hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="mb-2">
          <div className="flex items-center">
            <CurrencyIcon className="w-5 h-5 text-gray-900" />
            <span className="text-3xl font-normal text-gray-900">
              {displayPrice} /monthly
            </span>
          </div>
        </div>

        <p className="text-sm text-gray-600 line-clamp-3">
          {propertyDescription}
        </p>

        <motion.div
          whileHover={{ y: -8, boxShadow: "0 20px 40px rgba(85,99,246,0.15)" }}
          transition={{ type: "spring", stiffness: 300, damping: 22 }}
          className="relative bg-white rounded-3xl p-6 border border-gray-100 hover:border-[#5563F6]/30 flex flex-col h-[auto] gap-3"
        >
          <div className="flex justify-between text-sm text-gray-600 bg-gray-50 p-4 rounded-xl">
            <div>
              <p className="font-medium text-gray-900">Tour</p>
              <p>{tourDuration} min</p>
            </div>
            <div>
              <p className="font-medium text-gray-900">Buffer</p>
              <p>{bufferTime} min</p>
            </div>
          </div>
          {userType !== "worker" && (
            <button
              onClick={() => onEdit(listing)}
              className="w-full py-3 rounded-2xl bg-gradient-to-r from-[#5563F6] to-[#6C7BFF] text-white font-semibold"
            >
              Edit Listing
            </button>
          )}
        </motion.div>
      </motion.div>

      {userType !== "worker" && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleConfirmDelete}
          listingName={propertyName}
        />
      )}
    </>
  );
}

function AddWorkerModal({ service, onClose, onUpdated, userType }) {
  const [workers, setWorkers] = useState([]);
  const [selectedWorkers, setSelectedWorkers] = useState(service.workers || []);
  const [loading, setLoading] = useState(false);

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        setLoading(true);
        const headers = buildHeaders(token);
        
        const res = await fetch(`${API_URL}/auth/workers-list/`, { headers });
        if (!res.ok) throw new Error("Failed to load workers");
        const data = await res.json();
        setWorkers(
          data.filter((w) => w.full_name && w.full_name.trim() !== "")
        );
      } catch (err) {
        console.error(err);
        toast.error("Failed to load workers");
      } finally {
        setLoading(false);
      }
    };
    fetchWorkers();
  }, [token]);

  const toggleWorker = (id) => {
    setSelectedWorkers((prev) =>
      prev.includes(id) ? prev.filter((w) => w !== id) : [...prev, id]
    );
  };

  const handleSave = async () => {
    if (selectedWorkers.length === 0) {
      toast.error("Please select at least one worker");
      return;
    }
    try {
      setLoading(true);

      const headers = buildHeaders(token);
      
      const res = await fetch(`${API_URL}/service/my-services/${service.id}/`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          ...service,
          workers: selectedWorkers,
        }),
      });
      if (!res.ok) throw new Error("Failed to add worker");
      toast.success("Workers added successfully");

      const toggleRes = await fetch(
        `${API_URL}/service/my-services-toggle-on-off/`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ service_id: service.id }),
        }
      );
      if (!toggleRes.ok) throw new Error(await toggleRes.text());

      onUpdated();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Error adding worker or activating service");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        key="modal-backdrop"
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          key="modal-content"
          className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 flex flex-col gap-5"
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
        >
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">
              Add Worker to Service
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-10">
              <Loader2 className="w-8 h-8 animate-spin text-[#5563F6] mb-3" />
              <p className="text-gray-500">Loading workers...</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3 max-h-80 overflow-y-auto pr-2">
              <AnimatePresence>
                {workers.map((worker) => {
                  const isSelected = selectedWorkers.includes(worker.id);
                  return (
                    <motion.div
                      key={worker.id}
                      layout
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                        isSelected
                          ? "bg-[#EEF2FF] border border-[#5563F6]/20"
                          : "bg-gray-50 hover:bg-gray-100"
                      }`}
                      onClick={() => toggleWorker(worker.id)}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className="relative">
                          <img
                            src={worker.image_url}
                            alt={worker.full_name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                          />
                          {isSelected && (
                            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#5563F6] rounded-full border-2 border-white flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col flex-1">
                          <span className="font-semibold text-gray-900">
                            {worker.full_name}
                          </span>
                          <span className="text-sm text-gray-500 truncate">
                            {worker.worker_email}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#5563F6] to-[#6C7BFF] text-white font-medium hover:from-[#4453E3] hover:to-[#5A6AEA] transition-all shadow-lg shadow-[#5563F6]/30"
            >
              Link to Service
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function YourBusinessMyServices() {
  const [services, setServices] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingListings, setLoadingListings] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showWorkerModal, setShowWorkerModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedListing, setSelectedListing] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showWorkersView, setShowWorkersView] = useState(false);
  const [showAddListingModal, setShowAddListingModal] = useState(false);
  const [showEditListingModal, setShowEditListingModal] = useState(false);
  const [userType, setUserType] = useState(null);
  const [activeTab, setActiveTab] = useState("services");
  const [showDeleteServiceModal, setShowDeleteServiceModal] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!token) return;
      try {
        const headers = buildHeaders(token);
        
        const res = await fetch(`${API_URL}/auth/userinfo/`, { headers });
        const data = await res.json();
        setUserType(data?.user?.status || null);
      } catch (err) {
        console.error("Failed to fetch user info:", err);
      }
    };
    fetchUserInfo();
  }, [token]);

  const fetchServices = useCallback(async () => {
    try {
      setLoading(true);
      const headers = buildHeaders(token);
      
      const res = await fetch(`${API_URL}/service/my-services/`, { headers });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || "Failed to load services");
      }

      const data = await res.json();
      setServices(
        Array.isArray(data)
          ? data.map((s) => ({ ...s, on_off: s.on_off ?? false }))
          : []
      );
    } catch (err) {
      toast.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchListings = useCallback(async () => {
    try {
      setLoadingListings(true);
      const headers = buildHeaders(token);
      
      const res = await fetch(`${API_URL}/service/real-estate-my-listings/`, { headers });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();

      const normalizedListings = Array.isArray(data)
        ? data
        : data?.results || data?.data || [];

      if (normalizedListings.length > 0) {
       
      }

      setListings(normalizedListings);
    } catch (err) {
      toast.error("Failed to load listings");
      setListings([]);
    } finally {
      setLoadingListings(false);
    }
  }, [token]);

  const handleDeleteService = async () => {
    if (!serviceToDelete) return;

    try {
      const headers = buildHeaders(token);
      
      const res = await fetch(
        `${API_URL}/service/my-services/${serviceToDelete.id}/`,
        {
          method: "DELETE",
          headers,
        }
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to delete service");
      }

      toast.success("Service deleted successfully");
      fetchServices();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete service");
    } finally {
      setShowDeleteServiceModal(false);
      setServiceToDelete(null);
    }
  };

  const handleDeleteListing = async (listingId) => {
    try {
      const headers = buildHeaders(token);
      
      const res = await fetch(
        `${API_URL}/service/real-estate-my-listings/${listingId}/`,
        {
          method: "DELETE",
          headers,
        }
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to delete listing");
      }

      toast.success("Listing deleted successfully");
      fetchListings();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete listing");
    }
  };

  useEffect(() => {
    if (token) {
      if (activeTab === "services") {
        fetchServices();
      } else {
        fetchListings();
      }
    }
  }, [token, activeTab, fetchServices, fetchListings]);

  const formatDuration = (minutes) => {
    if (!minutes && minutes !== 0) return "N/A";
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  const toggleService = async (service) => {
    if (userType === "worker") return;

    if (userType === "individual") {
      try {
        const headers = buildHeaders(token);
        
        const res = await fetch(`${API_URL}/service/my-services-toggle-on-off/`, {
          method: "POST",
          headers,
          body: JSON.stringify({ service_id: service.id }),
        });

        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || "Failed to toggle service");
        }

        const data = await res.json();
        
        setServices((prev) =>
          prev.map((s) => (s.id === service.id ? { ...s, on_off: data.on_off } : s))
        );
        toast.success(`Service is now ${data.on_off ? "ON" : "OFF"}`);
      } catch (err) {
        console.error(err);
        toast.error("Failed to toggle service");
      }
      return;
    }

    if (!service.workers || service.workers.length === 0) {
      setSelectedService(service);
      setShowWorkerModal(true);
      return;
    }

    try {
      const headers = buildHeaders(token);
      
      const res = await fetch(`${API_URL}/service/my-services-toggle-on-off/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ service_id: service.id }),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to toggle service");
      }

      const data = await res.json();
      setServices((prev) =>
        prev.map((s) =>
          s.id === service.id ? { ...s, on_off: data.on_off } : s
        )
      );
      toast.success(`Service is now ${data.on_off ? "ON" : "OFF"}`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to toggle service");
    }
  };

  const formatServicePrice = (price, currencyCode = "USD") => {
    if (!price) return "—";

    const getCurrencySymbol = (code) => {
      const symbols = {
        USD: "$",
        EUR: "€",
        GBP: "£",
        RUB: "₽",
        ILS: "₪",
        AUD: "A$",
        CAD: "C$",
        BRL: "R$",
        MXN: "Mex$",
        AED: "د.إ",
      };
      return symbols[code] || "$";
    };

    const symbol = getCurrencySymbol(currencyCode);

    if (price >= 1_000_000) {
      const v = price / 1_000_000;
      return `${symbol}${v % 1 === 0 ? v.toFixed(0) : v.toFixed(2)}M`;
    }

    if (price >= 1_000) {
      const v = price / 1_000;
      return `${symbol}${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}K`;
    }

    return `${symbol}${price.toLocaleString()}`;
  };

  const handleListingCreated = () => {
    toast.success("Listing created successfully!");
    fetchListings();
    setShowAddListingModal(false);
  };

  const handleEditListing = (listing) => {
    setSelectedListing(listing);
    setShowEditListingModal(true);
  };

  const handleListingUpdated = () => {
    toast.success("Listing updated successfully!");
    fetchListings();
    setShowEditListingModal(false);
    setSelectedListing(null);
  };
  
  return (
    <div className="bg-white rounded-2xl shadow-sm p-6 w-full flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-2">
        <div className="relative flex bg-gray-100 rounded-2xl p-1 w-fit">
          <motion.div
            layout
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            className="absolute inset-y-1 w-1/2 rounded-xl bg-white shadow-sm"
            style={{
              left: activeTab === "services" ? "0.25rem" : "48%",
            }}
          />

          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            onClick={() => setActiveTab("services")}
            className={`relative z-10 flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-colors ${
              activeTab === "services"
                ? "text-[#5563F6]"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            <Briefcase className="w-4 h-4" />
            My Services
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            onClick={() => setActiveTab("listings")}
            className={`relative z-10 flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-colors ${
              activeTab === "listings"
                ? "text-[#5563F6]"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            <Home className="w-4 h-4" />
            My Listings
          </motion.button>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === "services" &&
            ["salon", "individual"].includes(userType) && (
              <motion.button
                key="create-service"
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 bg-gradient-to-r from-[#5563F6] to-[#6C7BFF] text-white text-base font-medium px-5 py-3 rounded-xl shadow-lg shadow-[#5563F6]/30 hover:shadow-xl hover:shadow-[#5563F6]/40 transition-all"
              >
                <Plus className="w-5 h-5" />
                Create Service
              </motion.button>
            )}

          {activeTab === "listings" && userType !== "worker" && (
            <motion.button
              key="add-listing"
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => setShowAddListingModal(true)}
              className="flex items-center gap-2 bg-gradient-to-r from-[#5563F6] to-[#6C7BFF] text-white text-base font-medium px-5 py-3 rounded-xl shadow-lg shadow-[#5563F6]/30 hover:shadow-xl hover:shadow-[#5563F6]/40 transition-all"
            >
              <Plus className="w-5 h-5" />
              Add Listing
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {activeTab === "services" && (
        <>
          {loading && (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-[#5563F6] animate-spin mb-3" />
              <p className="text-gray-500">Loading services...</p>
            </div>
          )}

          {!loading && services.length === 0 && (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No services found
              </h3>
              <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                Get started by creating your first service to offer to clients
              </p>
              {["salon", "individual"].includes(userType) && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-[#5563F6] to-[#6C7BFF] hover:from-[#4453E3] hover:to-[#5A6AEA] text-white font-medium px-5 py-2.5 rounded-xl transition-all shadow-lg shadow-[#5563F6]/30"
                >
                  <Plus className="w-4 h-4" />
                  Create Your First Service
                </button>
              )}
            </div>
          )}

          {!loading && services.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {services.map((service, index) => {
                const serviceCurrencyCode =
                  typeof service.currency === "string"
                    ? service.currency.toUpperCase()
                    : service.currency?.short_name?.toUpperCase() || "USD";

                const ServiceCurrencyIcon =
                  CURRENCY_ICON_MAP[serviceCurrencyCode] || FaDollarSign;

                return (
                  <motion.div
                    key={service.id || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{
                      y: -4,
                      boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                    }}
                    className="flex flex-col justify-between bg-white border border-gray-200 rounded-2xl shadow-sm p-5 transition-all hover:border-[#5563F6]/30"
                  >
                    <div className="flex flex-col gap-3 mb-4">
                      <div className="flex items-start justify-between gap-3">
                        <h4 className="font-bold text-lg text-gray-900 line-clamp-1 flex-1">
                          {service.name}
                        </h4>

                        <div className="flex items-center gap-3">
                          <div
                            className={`w-2.5 h-2.5 rounded-full ${
                              service.on_off ? "bg-green-500" : "bg-gray-300"
                            }`}
                          />

                          {["salon", "individual"].includes(userType) && (
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setServiceToDelete(service);
                                setShowDeleteServiceModal(true);
                              }}
                              className="text-gray-400 hover:text-red-500 p-2 rounded-lg hover:bg-red-50"
                              title="Delete service"
                            >
                              <Trash2 className="w-4 h-4" />
                            </motion.button>
                          )}
                        </div>
                      </div>

                      <p className="text-sm text-gray-600 leading-relaxed line-clamp-2 min-h-[40px]">
                        {service.description}
                      </p>
                    </div>

                    <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-4" />

                    <div className="flex items-center justify-between text-sm text-gray-700 mb-5">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-[#EEF2FF] flex items-center justify-center">
                          <Tag className="w-3.5 h-3.5 text-[#5563F6]" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Price</p>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-900">
                              {formatServicePrice(
                                service.price,
                                serviceCurrencyCode
                              )}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-[#EEF2FF] flex items-center justify-center">
                          <Clock className="w-3.5 h-3.5 text-[#5563F6]" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Duration</p>
                          <p className="font-semibold text-gray-900">
                            {formatDuration(service.duration)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 font-medium">
                          {service.on_off ? "Active" : "Inactive"}
                        </span>
                        <motion.button
                          onClick={() => toggleService(service)}
                          className={`w-11 h-6 rounded-full relative ${
                            userType === "worker"
                              ? "cursor-not-allowed opacity-50"
                              : ""
                          }`}
                          disabled={userType === "worker"}
                          initial={false}
                          animate={{
                            backgroundColor: service.on_off
                              ? "#5563F6"
                              : "#D1D5DB",
                          }}
                          transition={{
                            type: "spring",
                            stiffness: 500,
                            damping: 30,
                          }}
                        >
                          <motion.span
                            className="block w-4 h-4 bg-white rounded-full shadow-md absolute top-1"
                            layout
                            transition={{
                              type: "spring",
                              stiffness: 500,
                              damping: 30,
                            }}
                            style={{
                              left: service.on_off ? "1.625rem" : "0.375rem",
                            }}
                          />
                        </motion.button>
                      </div>

                      <div className="flex gap-2">
                        {userType === "salon" && (
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              setSelectedService(service);
                              setShowWorkersView(true);
                            }}
                            className="bg-gray-100 text-gray-700 text-sm px-3 py-2 rounded-lg font-medium transition hover:bg-gray-200"
                            title="View Workers"
                          >
                            <HiOutlineUserGroup className="w-4 h-4" />
                          </motion.button>
                        )}

                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            setSelectedService(service);
                            setShowEditModal(true);
                          }}
                          className="bg-[#5563F6] text-white text-sm px-4 py-2 rounded-lg font-medium transition hover:bg-[#4453E3]"
                        >
                          Edit
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </>
      )}

      {activeTab === "listings" && (
        <>
          {loadingListings && (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-[#5563F6] animate-spin mb-3" />
              <p className="text-gray-500">Loading listings...</p>
            </div>
          )}

          {!loadingListings && listings.length === 0 && (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Home className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No listings found
              </h3>
              <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                Start by adding your first real estate listing to showcase
                properties
              </p>
            </div>
          )}

          {!loadingListings && listings.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.map((listing, index) => (
                <ListingCard
                  key={listing.id || index}
                  listing={listing}
                  onEdit={handleEditListing}
                  onDelete={handleDeleteListing}
                  userType={userType}
                />
              ))}
            </div>
          )}
        </>
      )}

      {showEditModal && selectedService && (
        <EditServiceModal
          onClose={() => setShowEditModal(false)}
          service={selectedService}
          onUpdated={() => {
            fetchServices();
            setShowEditModal(false);
          }}
          role={userType}
          allServices={services}
        />
      )}

      {showWorkerModal && selectedService && (
        <AddWorkerModal
          service={selectedService}
          onClose={() => setShowWorkerModal(false)}
          onUpdated={fetchServices}
          userType={userType}
        />
      )}

      {showCreateModal && (
        <CreateServiceBussiness
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            fetchServices();
            setShowCreateModal(false);
          }}
        />
      )}

      {showWorkersView && selectedService && (
        <ShowWorkersModal
          service={selectedService}
          onClose={() => setShowWorkersView(false)}
          onUpdated={fetchServices}
        />
      )}

      {showAddListingModal && (
        <AddListingModal
          onClose={() => setShowAddListingModal(false)}
          onCreated={handleListingCreated}
        />
      )}

      {showEditListingModal && selectedListing && (
        <EditListingModal
          listing={selectedListing}
          onClose={() => {
            setShowEditListingModal(false);
            setSelectedListing(null);
          }}
          onUpdated={handleListingUpdated}
        />
      )}

      {showDeleteServiceModal && serviceToDelete && (
        <DeleteServiceConfirmationModal
          isOpen={showDeleteServiceModal}
          serviceName={serviceToDelete.name}
          onClose={() => {
            setShowDeleteServiceModal(false);
            setServiceToDelete(null);
          }}
          onConfirm={handleDeleteService}
        />
      )}
    </div>
  );
}